/**
 * React Query Hooks for Behavior Management
 * 
 * Provides caching, synchronization, and optimistic updates
 * for behavior-related data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import behaviorService from '../services/behaviorService';
import type { 
  BehaviorCase, 
  BehaviorNote, 
  BehaviorIncident, 
  CreateIncidentInput,
  StrategyAttempt,
  IncidentSummary
} from '../services/behaviorService';

// ═══════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════

export const behaviorKeys = {
  all: ['behavior'] as const,
  cases: () => [...behaviorKeys.all, 'cases'] as const,
  case: (id: string) => [...behaviorKeys.cases(), id] as const,
  notes: (caseId: string) => [...behaviorKeys.case(caseId), 'notes'] as const,
  incidents: (caseId: string) => [...behaviorKeys.case(caseId), 'incidents'] as const,
  incidentSummary: (caseId: string) => [...behaviorKeys.incidents(caseId), 'summary'] as const,
  strategies: (caseId: string) => [...behaviorKeys.case(caseId), 'strategies'] as const,
  strategyLibrary: () => [...behaviorKeys.all, 'strategyLibrary'] as const,
};

// ═══════════════════════════════════════════════════════════
// CASE HOOKS
// ═══════════════════════════════════════════════════════════

export function useBehaviorCases(params?: { 
  scope?: string; 
  studentId?: string; 
  groupId?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: behaviorKeys.cases(),
    queryFn: () => behaviorService.getCases(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useBehaviorCase(caseId: string | null) {
  return useQuery({
    queryKey: caseId ? behaviorKeys.case(caseId) : ['behavior', 'case', 'null'],
    queryFn: () => caseId ? behaviorService.getCase(caseId) : null,
    enabled: !!caseId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCreateCase() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: behaviorService.createCase,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: behaviorKeys.cases() });
    },
  });
}

export function useUpdateCase() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BehaviorCase> }) =>
      behaviorService.updateCase(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.case(variables.id) 
      });
    },
  });
}

// ═══════════════════════════════════════════════════════════
// NOTE HOOKS
// ═══════════════════════════════════════════════════════════

export function useNotes(caseId: string | null) {
  return useQuery({
    queryKey: caseId ? behaviorKeys.notes(caseId) : ['behavior', 'notes', 'null'],
    queryFn: () => caseId ? behaviorService.getNotes(caseId) : [],
    enabled: !!caseId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      caseId, 
      data 
    }: { 
      caseId: string; 
      data: Parameters<typeof behaviorService.createNote>[1] 
    }) => behaviorService.createNote(caseId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.notes(variables.caseId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.case(variables.caseId) 
      });
    },
  });
}

// ═══════════════════════════════════════════════════════════
// INCIDENT HOOKS
// ═══════════════════════════════════════════════════════════

export function useIncidents(
  caseId: string | null, 
  options?: { limit?: number; skip?: number }
) {
  return useQuery({
    queryKey: caseId 
      ? [...behaviorKeys.incidents(caseId), options] 
      : ['behavior', 'incidents', 'null'],
    queryFn: () => caseId 
      ? behaviorService.getIncidents(caseId, options) 
      : [],
    enabled: !!caseId,
    staleTime: 30 * 1000, // 30 seconds - incidents change frequently
  });
}

export function useIncidentSummary(
  caseId: string | null, 
  days: number = 30
) {
  return useQuery({
    queryKey: caseId 
      ? [...behaviorKeys.incidentSummary(caseId), days] 
      : ['behavior', 'incidentSummary', 'null'],
    queryFn: () => caseId 
      ? behaviorService.getIncidentSummary(caseId, days) 
      : null,
    enabled: !!caseId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCreateIncident() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      caseId, 
      data 
    }: { 
      caseId: string; 
      data: CreateIncidentInput 
    }) => behaviorService.createIncident(caseId, data),
    onSuccess: (_, variables) => {
      // Invalidate all incident-related queries
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.incidents(variables.caseId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.incidentSummary(variables.caseId) 
      });
      // Also update case stats
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.case(variables.caseId) 
      });
    },
  });
}

// ═══════════════════════════════════════════════════════════
// STRATEGY HOOKS
// ═══════════════════════════════════════════════════════════

export function useStrategies(caseId: string | null) {
  return useQuery({
    queryKey: caseId 
      ? behaviorKeys.strategies(caseId) 
      : ['behavior', 'strategies', 'null'],
    queryFn: () => caseId 
      ? behaviorService.getStrategies(caseId) 
      : [],
    enabled: !!caseId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCreateStrategyAttempt() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      caseId, 
      data 
    }: { 
      caseId: string; 
      data: Parameters<typeof behaviorService.createStrategyAttempt>[1] 
    }) => behaviorService.createStrategyAttempt(caseId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.strategies(variables.caseId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.case(variables.caseId) 
      });
    },
  });
}

export function useUpdateStrategyAttempt() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      strategyId, 
      data 
    }: { 
      strategyId: string; 
      data: Partial<StrategyAttempt>;
      caseId: string;
    }) => behaviorService.updateStrategyAttempt(strategyId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: behaviorKeys.strategies(variables.caseId) 
      });
    },
  });
}

export function useStrategyLibrary(query?: string, tier?: 1 | 2 | 3) {
  return useQuery({
    queryKey: [...behaviorKeys.strategyLibrary(), query, tier],
    queryFn: () => behaviorService.getStrategyLibrary(query, tier),
    staleTime: 10 * 60 * 1000, // 10 minutes - library rarely changes
  });
}
